/* eslint-disable jsx-a11y/label-has-associated-control */
import { useContext, useState } from 'react';
import { BsCheck2 } from 'react-icons/bs';
import { add, format, isAfter } from 'date-fns';
import PropTypes from 'prop-types';
import { Button, ButtonDropdown, SelectGeneric } from 'ypf-pas-general-ui-components';
import { MdFilterAlt, MdOutlineRemoveCircleOutline } from 'react-icons/md';
import { es } from 'date-fns/locale';
import { IoIosRefresh } from 'react-icons/io';
import { BiXCircle } from 'react-icons/bi';

import { getDaysInterval } from '../../utils/date';
import baseStyles from '../../styles/base.module.scss';
import { approveWorkReports, rejectWorkReports } from '../../../../services/workReport';
import useToast from '../../../../hooks/useToast';
import InputCalendar from '../../../Calendar/InputCalendar/InputCalendar';
import LoaderContext from '../../../../context/LoaderProvider';
import { useWorkReportModals } from '../../../../context/WorkReportModalsContext';
import { SORT_OPTIONS, locationSort } from '../../utils/consts';

import styles from './TableHeader.module.scss';

/**
 * @typedef Props
 * @property {boolean} areCheckedDays
 * @property {import('../../../../adapters/weekly-report/get-weekly-report-adapter').TableDataCalendar} calendar
 * @property {boolean} extendedView
 * @property {() => Promise<import('../../../../adapters/weekly-report/get-weekly-report-adapter').TableData>} fetchAllData
 * @property {boolean} filledFilter
 * @property {import('../../hooks/useFilters').Filters} filters
 * @property {() => number[]} getIDsToSend
 * @property {(newData: import('../../../../adapters/weekly-report/get-weekly-report-adapter').TableData) => void} handleCheckAll
 * @property {() => void} handleUpdateApprovedOrRejectedCells
 * @property {Date} initialDay
 * @property {boolean} isFetchingNextPage
 * @property {boolean} isInspector
 * @property {boolean} isLoading
 * @property {boolean} isTableChecked
 * @property {import('react').Dispatch<import('react').SetStateAction<boolean>>} setShowFilters
 * @property {(newStartDay: Date) => void} updateStartDay
 * @property {() => void} handleReloadTable
 */

/** @param {Props} */
const TableHeader = ({
  areCheckedDays,
  calendar,
  changeSortType,
  extendedView,
  filledFilter,
  filters,
  // fetchAllData,
  getIDsToSend,
  // handleCheckAll,
  handleUpdateApprovedOrRejectedCells,
  initialDay,
  // isFetchingNextPage,
  isInspector,
  isLoading,
  // isTableChecked,
  resetDaysToApproveOrReject,
  setShowFilters,
  updateStartDay,
  sortType,
  handleReloadTable
}) => {
  const { setLoader } = useContext(LoaderContext);
  const { activeApproveSelectedQuantitiesModal, activeRejectSelectedQuantitiesModal } =
    useWorkReportModals();
  const { setNewToast } = useToast();
  const dateFormat = 'EEEE dd';
  const [sortValue, setSortValue] = useState(
    sortType === locationSort ? SORT_OPTIONS[0] : SORT_OPTIONS[1]
  );

  const initialDate = initialDay ? new Date(initialDay) : new Date();

  const [days, setDays] = useState(
    getDaysInterval({
      start: initialDate,
      end: add(initialDate, { days: 6 })
    })
  );

  const [isApproving, setIsApproving] = useState(false);

  const agrupaPorUbicacion = sortType === 'location';

  const handleWeekChange = (newDate) => {
    if (isLoading) return;

    if (newDate instanceof Date) {
      updateStartDay(newDate);
      setDays(getDaysInterval({ start: newDate, end: add(newDate, { days: 6 }), dateFormat }));

      return;
    }

    if (newDate?.$d) {
      updateStartDay(newDate.$d);
      setDays(
        getDaysInterval({ start: newDate.$d, end: add(newDate.$d, { days: 6 }), dateFormat })
      );
    }
  };

  // /** @param {string} date */
  // const handleFirstAndLastDay = (date) => {
  //   if (isLoading) return;

  //   if (isSameDay(initialDate, new Date(calendar.firstWeek.startDate))) return;

  //   const currentURL = window.location.href;
  //   const partToReplace = format(initialDate, 'yyyy-MM-dd');

  //   const newURL = currentURL.replace(partToReplace, format(new Date(date), 'yyyy-MM-dd'));

  //   window.history.pushState(null, '', newURL);

  //   setDays(
  //     getDaysInterval({
  //       start: new Date(date),
  //       end: add(new Date(date), { days: 6 }),
  //       dateFormat
  //     })
  //   );
  //   updateStartDay(new Date(date));
  // };

  // const handleSelectAll = async () => {
  //   setLoader(true);
  //   const newData = await fetchAllData();

  //   handleCheckAll(newData);
  //   setLoader(false);
  // };

  const hasDaysToApprove = () => {
    const idToSend = getIDsToSend();

    return idToSend && idToSend.length !== 0;
  };

  const handleApprove = () => {
    const workReportIDs = getIDsToSend();

    setIsApproving(true);
    setLoader(true);
    approveWorkReports({ partesDiariosIds: workReportIDs }, isInspector)
      .then(() => {
        setNewToast({ type: 'SUCCESS', message: 'Se han enviado los partes' });
        handleUpdateApprovedOrRejectedCells();
      })
      .catch((error) => setNewToast({ type: 'ERROR', message: error.message }))
      .finally(() => {
        setIsApproving(false);
        setLoader(false);
      });
  };

  const handleReject = () => {
    const workReportIDs = getIDsToSend();

    setLoader(true);
    rejectWorkReports({ partesDiariosIds: workReportIDs })
      .then((message) => {
        setNewToast({ type: 'SUCCESS', message });
        handleUpdateApprovedOrRejectedCells();
      })
      .catch((error) => setNewToast({ type: 'ERROR', message: error.message }))
      .finally(() => {
        setLoader(false);
      });
  };

  const handleAuthorizationButtonClick = () => {
    if (isInspector) {
      const idsPartesDiario = getIDsToSend();

      activeApproveSelectedQuantitiesModal({ idsPartesDiario, agrupaPorUbicacion, handleApprove });
    } else {
      handleApprove();
    }
  };

  const handleRejectButtonClick = () => {
    const idsPartesDiario = getIDsToSend();

    activeRejectSelectedQuantitiesModal({ idsPartesDiario, agrupaPorUbicacion, handleReject });
  };

  // Disable calendar when dates filter are not setted
  const initialMaxDate = new Date();
  const initialMinDate = add(initialMaxDate, { days: 3 });
  const isDisabled = !filters.fechaDesde || !filters.fechaHasta;

  const firstColumnClassName = `${styles['first-column-container']} ${
    isDisabled ? 'calendar-disabled' : ''
  }`;

  return (
    <thead>
      <tr>
        <th aria-label="calendar" className={styles.th}>
          <div className={firstColumnClassName}>
            <Button
              className={filledFilter ? 'btn-filter-active' : 'btn-filter-inactive'}
              icon={MdFilterAlt}
              onClick={() => setShowFilters(true)}
              sizeIcon="20px"
              type="button"
              variant="white"
              widthButton="40px"
            />
            <InputCalendar
              arrayDate={isLoading ? [] : calendar.pendingDates}
              extendedView={extendedView}
              firstDayOfWeek={`${format(new Date(initialDate), 'yyyy-MM-dd')}T00:00:00`}
              handleChangeDate={handleWeekChange}
              lastDayOfWeek={`${format(
                add(new Date(initialDate), { days: 6 }),
                'yyyy-MM-dd'
              )}T00:00:00`}
              maxDate={
                filters.fechaHasta || `${format(new Date(initialMaxDate), 'yyyy-MM-dd')}T00:00:00`
              }
              minDate={
                filters.fechaDesde || `${format(new Date(initialMinDate), 'yyyy-MM-dd')}T00:00:00`
              }
              selectDay={`${format(new Date(initialDate), 'yyyy-MM-dd')}T00:00:00`}
              view="weekly"
              weeklyReportTable
            />
            <SelectGeneric
              className={styles['group-select']}
              name="sortType"
              onChange={(e) => {
                setSortValue(e);
                if (e.value === 'provider') {
                  changeSortType('provider');
                } else {
                  changeSortType('location');
                }
              }}
              optionMapper={{
                value: 'value',
                label: 'label',
                nombre: 'nombre',
                id: 'id'
              }}
              options={SORT_OPTIONS}
              value={sortValue}
              withSelect="130px"
            />
            <ButtonDropdown
              label="Opciones"
              options={[
                {
                  label: 'Borrar selección',
                  onClick: resetDaysToApproveOrReject,
                  disabled: !hasDaysToApprove(),
                  icon: <MdOutlineRemoveCircleOutline />,
                  tooltipText: 'Reiniciar selección'
                },
                isInspector && {
                  label: 'Rechazar',
                  onClick: handleRejectButtonClick,
                  disabled: isLoading || !areCheckedDays,
                  icon: <BiXCircle />,
                  tooltipText: 'Rechazar'
                },
                {
                  label: 'Recargar datos',
                  onClick: handleReloadTable,
                  icon: <IoIosRefresh />,
                  tooltipText: 'Recargar datos'
                }
              ]}
            />
            <button
              className={`${baseStyles['focus-ring']} ${styles['btn-weekly-approve']}`}
              disabled={isLoading || isApproving || !areCheckedDays}
              onClick={handleAuthorizationButtonClick}
              type="button"
            >
              {isInspector ? 'Autorizar ' : 'Aprobar'} <BsCheck2 />
            </button>
          </div>
        </th>
        {days?.map((day, index) => {
          const { fechaDesde, fechaHasta } = filters;

          const startDay = add(new Date(fechaDesde), { days: -1 });
          const endDay = add(new Date(fechaHasta), { days: 1 });

          const isInDateRange = isAfter(day, startDay) && isAfter(endDay, day);

          const isDate = day instanceof Date;

          return (
            <th className={styles.th} data-day-cell-disabled={!isInDateRange} key={day}>
              <div className={styles['table-header-day']} data-day={index + 1}>
                {isDate ? format(day, dateFormat, { locale: es }) : day}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

TableHeader.propTypes = {
  areCheckedDays: PropTypes.bool,
  calendar: PropTypes.shape({
    firstWeek: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string
    }),
    lastWeek: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string
    }),
    pendingDates: PropTypes.arrayOf(PropTypes.string)
  }),
  changeSortType: PropTypes.func.isRequired,
  filledFilter: PropTypes.bool.isRequired,
  filters: PropTypes.shape({
    contratosIds: PropTypes.arrayOf(PropTypes.number),
    partesVisualizar: PropTypes.bool,
    idsEquipos: PropTypes.arrayOf(PropTypes.number),
    idsEventos: PropTypes.arrayOf(PropTypes.number),
    idsProveedores: PropTypes.arrayOf(PropTypes.number),
    fechaDesde: PropTypes.string,
    fechaHasta: PropTypes.string
  }),
  // fetchAllData: PropTypes.func,
  getIDsToSend: PropTypes.func,
  // handleCheckAll: PropTypes.func,
  handleUpdateApprovedOrRejectedCells: PropTypes.func,
  initialDay: PropTypes.instanceOf(Date).isRequired,
  // isFetchingNextPage: PropTypes.bool,
  isInspector: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  // isTableChecked: PropTypes.bool,
  resetDaysToApproveOrReject: PropTypes.func.isRequired,
  setShowFilters: PropTypes.func.isRequired,
  sortType: PropTypes.string.isRequired,
  updateStartDay: PropTypes.func,
  extendedView: PropTypes.bool,
  handleReloadTable: PropTypes.func
};

TableHeader.defaultProps = {
  areCheckedDays: false,
  calendar: {},
  // fetchAllData: () => {},
  getIDsToSend: () => {},
  // handleCheckAll: () => {},
  handleUpdateApprovedOrRejectedCells: () => {},
  // isFetchingNextPage: false,
  isLoading: false,
  filters: {},
  // isTableChecked: false,
  updateStartDay: () => {},
  extendedView: false,
  handleReloadTable: () => {}
};

export default TableHeader;
